import dayjs from "dayjs";
import {
  AssignmentProposalAction,
  AssignmentProposalStatus,
  type AssignmentProposal,
  type AssignmentProposalDimension,
} from "@models/ISchema";
import { userDisplayName } from "@services/collections/assignment-proposal/assignmentProposalAdapters";
import { buildProposalChangeGroups } from "@services/collections/assignment-proposal/assignmentProposalListAdapters";
import type {
  ProposalChangeGroup,
  ProposalChangeLine,
} from "@services/types/assignment-proposal/assignmentProposalListTypes";
import type { ProposalScheduleValue } from "@services/types/assignment-proposal/assignmentProposalTypes";

export type ProposalCardAction = "continue" | "view" | "correct" | "review";

export type ProposalCardStatusTone = "draft" | "pending" | "approved" | "rejected";

/** Everything a card of the proposals Drawer renders, already decided. */
export interface ProposalCardModel {
  id: string;
  /** Key under group-proposals.status; null for a state the front does not know. */
  statusKey: string | null;
  statusTone: ProposalCardStatusTone | null;
  action: ProposalCardAction | null;
  dimensions: AssignmentProposalDimension[];
  /** False when the backend could not compute the changes: no pills and no detail. */
  hasSummary: boolean;
  changeGroups: ProposalChangeGroup[];
  /** The chosen intermediate approver. */
  reviewerName: string | null;
  rejectionReason: string | null;
  /** Creation date, DD-MM-YYYY in the browser's time zone. */
  createdOn: string | null;
  /** Sent proposals: who created and sent it (null while the backend does not return it). */
  creatorName: string | null;
  /** Review cards: the user may reject it from the card. */
  canReject: boolean;
  /** Requester drafts: the user may delete it from the card. */
  canDelete: boolean;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

const CHANGES_KEY_PREFIX = "group-proposals.changes";

/** "17:30:00" → "17:30". */
const toHoursAndMinutes = (time: string | null | undefined): string => (time ?? "").slice(0, 5);

/**
 * A schedule as the Drawer reads it: its day and its time range, or the number of blocks of a
 * session without schedule. Day names come from the active language.
 */
export const formatProposalSchedule = (schedule: ProposalScheduleValue, t: Translate): string => {
  if (schedule?.mode === "NO_SCHEDULE") {
    return t(`${CHANGES_KEY_PREFIX}.no-schedule`, { count: schedule.no_schedule_event_count });
  }

  const day = schedule?.day ? t(`day-list.full-names.${String(schedule.day).toLowerCase()}`) : "";
  const range = `${toHoursAndMinutes(schedule?.start_time)}–${toHoursAndMinutes(schedule?.end_time)}`;

  return `${day} ${range}`.trim();
};

/** One line of the detail: "Agrega Sala: A-201", "Elimina Horario: Lunes 17:30–20:30". */
export const formatProposalChangeLine = (line: ProposalChangeLine, t: Translate): string => {
  const label = t(`${CHANGES_KEY_PREFIX}.${line.key}`);
  const detail = line.schedule ? formatProposalSchedule(line.schedule, t) : (line.value ?? "");

  return detail ? `${label}: ${detail}` : label;
};

/** Heading of the session a group of lines belongs to: "Sesión 3384830", "Sesión nueva". */
export const formatProposalChangeHeading = (heading: ProposalChangeLine, t: Translate): string =>
  [t(`${CHANGES_KEY_PREFIX}.${heading.key}`), heading.value].filter(Boolean).join(" ");

const STATUS_VIEW: Record<
  AssignmentProposalStatus,
  { statusKey: string; statusTone: ProposalCardStatusTone; action: ProposalCardAction }
> = {
  [AssignmentProposalStatus.Draft]: { statusKey: "draft", statusTone: "draft", action: "continue" },
  [AssignmentProposalStatus.InReviewIntermediate]: {
    statusKey: "in-review-intermediate",
    statusTone: "pending",
    action: "view",
  },
  [AssignmentProposalStatus.IntermediateApproved]: {
    statusKey: "intermediate-approved",
    statusTone: "approved",
    action: "view",
  },
  [AssignmentProposalStatus.Rejected]: {
    statusKey: "rejected",
    statusTone: "rejected",
    action: "correct",
  },
};

const formatDate = (isoDate: string | null | undefined): string | null => {
  if (!isoDate) return null;

  const date = dayjs(isoDate);

  return date.isValid() ? date.format("DD-MM-YYYY") : null;
};

/**
 * Everything a card of the proposals Drawer shows, decided from the backend proposal. A review
 * card ("Por revisar") always opens the review. Every card shows when it was created, a sent
 * proposal also by whom, and a rejected one (out of "Por revisar") the rejection comment.
 */
export const toProposalCardModel = (
  proposal: AssignmentProposal,
  { isReview = false }: { isReview?: boolean } = {},
): ProposalCardModel => {
  const view = proposal.status ? STATUS_VIEW[proposal.status] : undefined;
  const isProposalDraft = proposal.status === AssignmentProposalStatus.Draft;
  // The backend answers null (not an empty list) when it could not compute the changes.
  const hasSummary = Array.isArray(proposal.changes);
  const reviewer = proposal.requestedReviewers?.[0];
  const rejectionReason =
    !isReview && proposal.status === AssignmentProposalStatus.Rejected
      ? proposal.rejectionReason?.trim() || null
      : null;

  return {
    id: proposal.id,
    statusKey: view?.statusKey ?? null,
    statusTone: view?.statusTone ?? null,
    action: !view ? null : isReview ? "review" : view.action,
    dimensions: hasSummary ? (proposal.affectedDimensions ?? []) : [],
    hasSummary,
    changeGroups: hasSummary ? buildProposalChangeGroups(proposal.changes) : [],
    reviewerName: reviewer ? userDisplayName(reviewer) : null,
    rejectionReason,
    createdOn: formatDate(proposal.createdAt),
    creatorName:
      !isProposalDraft && proposal.createdBy ? userDisplayName(proposal.createdBy) : null,
    canReject:
      isReview && !!view && !!proposal.allowedActions?.includes(AssignmentProposalAction.Reject),
    // Only a draft that was never sent: the backend refuses the rest (PROPOSAL_NOT_EDITABLE).
    canDelete: !isReview && isProposalDraft,
  };
};
